/**
 * Demo Scenarios Dialog
 *
 * App-bar demo dialog, split into Predict and Coordinate tabs. The previous
 * (full) demo controls now live in RyansScenariosDialog.
 */

import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Button,
  Box,
  IconButton,
  FormControl,
  MenuItem,
  Select,
  Tooltip,
  Tabs,
  Tab,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import MonitorIcon from '@mui/icons-material/Monitor';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import { useNavigate } from 'react-router-dom';
import { usePredictContext } from '~/hooks/predict/usePredictContext';
import { useDischargeContext } from '~/hooks/discharge/useDischargeContext';
import { buildQualifyChecks } from '~/utils/qualifyChecks';
import { PredictProgram } from '~/types/predict';

type DemoTab = 'predict' | 'coordinate';

export interface DemoScenariosDialogProps {
  open: boolean;
  onClose: () => void;
}

export function DemoScenariosDialog({ open, onClose }: DemoScenariosDialogProps) {
  const navigate = useNavigate();
  const { patients, updatePatient } = usePredictContext();
  const { patients: dischargePatients, sendToFollowUp } = useDischargeContext();

  const [activeTab, setActiveTab] = useState<DemoTab>('predict');
  const [hahPatientId, setHahPatientId] = useState<string>('');
  const [qualifyPatientId, setQualifyPatientId] = useState<string>('');
  const [followUpPatientId, setFollowUpPatientId] = useState<string>('');

  // The transfer order comes through the EHR: mark it received on the patient and
  // drop them into the Hospital at Home intake queue (the /intake page) — the
  // same effect selecting Hospital at Home in Predict used to have.
  const handleTriggerHahAdmission = () => {
    const patient = patients.find((p) => p.id === hahPatientId);
    if (!patient) {
      return;
    }
    updatePatient(patient.id, {
      selectedProgram: PredictProgram.HOSPITAL_AT_HOME,
      scheduledAt: undefined,
      transferOrder: {
        entered: true,
        physician: patient.attendingPhysician,
        enteredAt: new Date().toISOString(),
      },
    });
    onClose();
  };

  // Simulate the EHR qualification forms coming back positive: mark the three
  // EHR-driven qualify checks as verified for the selected patient.
  const handleQualifyFromEhr = () => {
    const patient = patients.find((p) => p.id === qualifyPatientId);
    if (!patient) {
      return;
    }
    updatePatient(patient.id, {
      qualifyChecks: buildQualifyChecks(patient, new Date().toISOString(), 'all'),
    });
    onClose();
  };

  // Take the selected patient through discharge into the post-discharge
  // follow-up phase, then open their detail so the agency update is visible.
  const handleTriggerFollowUp = () => {
    if (!followUpPatientId) {
      return;
    }
    sendToFollowUp(followUpPatientId);
    onClose();
    navigate(`/discharge/patient/${followUpPatientId}`);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { minHeight: '80vh' },
      }}
    >
      <DialogTitle sx={{ pb: 0 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="h5" component="span">
            Demo Scenarios
          </Typography>
          <IconButton onClick={onClose} edge="end">
            <CloseIcon />
          </IconButton>
        </Box>
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v as DemoTab)}>
          <Tab label="Predict" value="predict" sx={{ textTransform: 'none' }} />
          <Tab label="Coordinate" value="coordinate" sx={{ textTransform: 'none' }} />
        </Tabs>
      </DialogTitle>

      <DialogContent>
        {activeTab === 'predict' && (
          <>
            {/* Qualify Patient from EHR */}
            <Box sx={{ mt: 1, pt: 3, borderTop: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="subtitle2" fontWeight={700}>Qualify Patient from EHR</Typography>
                <Typography variant="body2" color="text.secondary">
                  All necessary qualification forms are filled out in the EHR with a positive result
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 2, flexShrink: 0 }}>
                <FormControl size="small" sx={{ minWidth: 220 }}>
                  <Select
                    value={qualifyPatientId}
                    onChange={(e) => setQualifyPatientId(e.target.value)}
                    displayEmpty
                    renderValue={(selected) => {
                      if (!selected) {
                        return <Box component="span" sx={{ color: 'text.disabled' }}>Select patient</Box>;
                      }
                      const p = patients.find((pt) => pt.id === selected);
                      return p ? `${p.firstName} ${p.lastName}` : selected;
                    }}
                  >
                    <MenuItem value="">
                      <em>Select patient</em>
                    </MenuItem>
                    {patients.map((p) => (
                      <MenuItem key={p.id} value={p.id}>
                        {p.firstName} {p.lastName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Tooltip title="Qualify patient">
                  <span>
                    <IconButton
                      color="primary"
                      onClick={handleQualifyFromEhr}
                      disabled={!qualifyPatientId}
                    >
                      <PlayCircleIcon sx={{ fontSize: 40 }} />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            </Box>

            {/* Hospital at Home Admission Demo */}
            <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="subtitle2" fontWeight={700}>Hospital at Home Admission Demo</Typography>
                <Typography variant="body2" color="text.secondary">
                  An order is placed in the EHR referring a patient from Predict to the hospital at home program
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 2, flexShrink: 0 }}>
                <FormControl size="small" sx={{ minWidth: 220 }}>
                  <Select
                    value={hahPatientId}
                    onChange={(e) => setHahPatientId(e.target.value)}
                    displayEmpty
                    renderValue={(selected) => {
                      if (!selected) {
                        return <Box component="span" sx={{ color: 'text.disabled' }}>Select patient</Box>;
                      }
                      const p = patients.find((pt) => pt.id === selected);
                      return p ? `${p.firstName} ${p.lastName}` : selected;
                    }}
                  >
                    <MenuItem value="">
                      <em>Select patient</em>
                    </MenuItem>
                    {patients.map((p) => (
                      <MenuItem key={p.id} value={p.id}>
                        {p.firstName} {p.lastName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Tooltip title="Trigger admission">
                  <span>
                    <IconButton
                      color="primary"
                      onClick={handleTriggerHahAdmission}
                      disabled={!hahPatientId}
                    >
                      <PlayCircleIcon sx={{ fontSize: 40 }} />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            </Box>

            {/* Predict EHR Integration Demo */}
            <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="subtitle2" fontWeight={700}>Predict EHR Integration Demo</Typography>
                <Typography variant="body2" color="text.secondary">
                  Mock EHR UI demonstrating MedArrive's SMART on FHIR integration with Predict
                </Typography>
              </Box>
              <Button
                variant="outlined"
                startIcon={<MonitorIcon />}
                onClick={() => { onClose(); navigate('/ehr-demo/predict'); }}
                sx={{ ml: 2, whiteSpace: 'nowrap', flexShrink: 0 }}
              >
                View EHR Demo
              </Button>
            </Box>
          </>
        )}

        {activeTab === 'coordinate' && (
          <>
            {/* Post-Discharge Agency Follow-Up */}
            <Box sx={{ mt: 1, pt: 3, borderTop: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="subtitle2" fontWeight={700}>Post-Discharge Agency Follow-Up</Typography>
                <Typography variant="body2" color="text.secondary">
                  Complete discharge and have the automated agent call the agency for a status update on the patient
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 2, flexShrink: 0 }}>
                <FormControl size="small" sx={{ minWidth: 220 }}>
                  <Select
                    value={followUpPatientId}
                    onChange={(e) => setFollowUpPatientId(e.target.value)}
                    displayEmpty
                    renderValue={(selected) => {
                      if (!selected) {
                        return <Box component="span" sx={{ color: 'text.disabled' }}>Select patient</Box>;
                      }
                      const p = dischargePatients.find((pt) => pt.id === selected);
                      return p ? p.name : selected;
                    }}
                  >
                    <MenuItem value="">
                      <em>Select patient</em>
                    </MenuItem>
                    {dischargePatients.map((p) => (
                      <MenuItem key={p.id} value={p.id}>
                        {p.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Tooltip title="Complete discharge and collect agency update">
                  <span>
                    <IconButton
                      color="primary"
                      onClick={handleTriggerFollowUp}
                      disabled={!followUpPatientId}
                    >
                      <PlayCircleIcon sx={{ fontSize: 40 }} />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            </Box>

            {/* Coordinate EHR Integration Demo */}
            <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="subtitle2" fontWeight={700}>Coordinate EHR Integration Demo</Typography>
                <Typography variant="body2" color="text.secondary">
                  Mock EHR UI demonstrating MedArrive's SMART on FHIR integration with Coordinate
                </Typography>
              </Box>
              <Button
                variant="outlined"
                startIcon={<MonitorIcon />}
                onClick={() => { onClose(); navigate('/ehr-demo/coordinate'); }}
                sx={{ ml: 2, whiteSpace: 'nowrap', flexShrink: 0 }}
              >
                View EHR Demo
              </Button>
            </Box>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
